import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { createBedBathSqFtTableData } from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';

import DetailTableInfo from '../Detail/DetailTable';
import FloorPlanPrintIndividualPage from './FloorPlanPrintIndividualPage';
import FloorPlanFinancialPlans from '../FloorPlan/FloorPlanFinancialPlans';

import './FloorPlanPrintPage.scss';

const auth = getAuthClient();

const FloorPlanPrintPage = () => {
  const [floorPlanData, setFloorPlanData] = useState({});
  const [tableInfo, setTableInfo] = useState([]);
  const [threeDLoaded, setThreeDLoaded] = useState(false);
  const [twoDLoaded, setTwoDLoaded] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  const { drupalNid } = useParams();

  // Get floor plan and community data using optimized endpoint.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = buildYourTourApiUrl(`/your_tour_api/floor_plan/${drupalNid}`);
        const data = await auth.fetchWithAuthentication(url, 'GET', null);

        if (data && !data.error) {
          setFloorPlanData(data);
        }
      } catch (error) {
        console.error('Error fetching floor plan data:', error);
      }
    };
    if (drupalNid) {
      fetchData();
    }
  }, [drupalNid]);

  const {
    title,
    description,
    roomData,
    threeD,
    twoD,
    feeData,
    community,
  } = floorPlanData || {};

  const threeDSrc = threeD?.modal;
  const twoDSrc = twoD?.modal;
  const logoSrc = community?.logo;

  // Format pricing data for main table.
  useEffect(() => {
    const rawTableInfo = createBedBathSqFtTableData(floorPlanData);
    setTableInfo(rawTableInfo);
  }, [floorPlanData]);

  // Set images as loaded if they don't exist.
  const hasFloorPlanData = Object.keys(floorPlanData).length > 0;
  useEffect(() => {
    if (hasFloorPlanData) {
      if (!threeDSrc) {
        setThreeDLoaded(true);
      }
      if (!twoDSrc) {
        setTwoDLoaded(true);
      }
      if (!logoSrc) {
        setLogoLoaded(true);
      }
    }
  }, [hasFloorPlanData,
    threeDSrc,
    twoDSrc]);

  // Don't open print dialog until fp images have loaded.
  useEffect(() => {
    if (twoDLoaded && threeDLoaded && logoLoaded) {
      window.print();
    }
  }, [twoDLoaded,
    threeDLoaded]);

  return (
    <div className="floorPlanPrintPage">

      {threeDSrc && (
        <FloorPlanPrintIndividualPage
          floorPlanData={floorPlanData}
          logoLoaded={logoLoaded}
          setLogoLoaded={setLogoLoaded}
        >
          <img
            alt={`${title} 3D Floor Plan`}
            className="floorPlanPrintPage__image"
            src={threeDSrc}
            onLoad={() => {
              if (!threeDLoaded) {
                setThreeDLoaded(true);
              }
            }}
          />
        </FloorPlanPrintIndividualPage>
      )}

      {twoDSrc && (
        <FloorPlanPrintIndividualPage
          floorPlanData={floorPlanData}
          logoLoaded={logoLoaded}
          setLogoLoaded={setLogoLoaded}
        >
          <img
            alt={`${title} 2D Floor Plan`}
            className="floorPlanPrintPage__image"
            src={twoDSrc}
            onLoad={() => {
              if (!twoDLoaded) {
                setTwoDLoaded(true);
              }
            }}
          />
        </FloorPlanPrintIndividualPage>
      )}

      {floorPlanData && (
        <FloorPlanPrintIndividualPage
          floorPlanData={floorPlanData}
          logoLoaded={logoLoaded}
          setLogoLoaded={setLogoLoaded}
        >
          <DetailTableInfo
            className="detailSection"
            listData={tableInfo}
            tableDescription={description}
          />

          <FloorPlanFinancialPlans
            feeData={feeData}
            className="detailSection"
          />

          <DetailTableInfo
            className="detailSection"
            listData={roomData}
          />

          <div className="floorPlanPrintPage__notes">
            <h2 className="floorPlanPrintPage__notesTitle">Notes</h2>

            {Array.from({ length: 20 }, (x, i) => <div key={`line${i}`} className="floorPlanPrintPage__notesLine" />)}

          </div>

        </FloorPlanPrintIndividualPage>
      )}

    </div>
  );
};

export default FloorPlanPrintPage;
